/**
 * El filtro del panel en la peticion: el mismo para el snapshot y para cada lista.
 *
 * El periodo lo resuelve el SERVIDOR (en la zona del inquilino, en cada
 * peticion): el panel manda el preset y, solo si es a medida, los dos dias. Las
 * personas van como `personas=todas` o como ids (`personas=a,b,sin_asignar`).
 * Para un servidor de antes (durante un despliegue) van tambien `from`/`to` y
 * `scope=team`, que el nuevo ignora cuando tiene preset y personas.
 *
 * Las listas que abre una tarjeta piden EXACTAMENTE lo que el snapshot aplico (el
 * eco `snapshot.filtro`), no lo que el selector tenia al cargar: asi la lista
 * tiene las mismas filas que suma la tarjeta.
 *
 * Lo elegido en el desplegable de personas se recuerda por quien mira (por
 * inquilino y usuario) en el navegador. Es una comodidad: si el almacenamiento
 * no esta (ventana privada, iframe sin permiso) se ve "Todas", que es lo de
 * por defecto, y nada se rompe.
 */
import type { PersonaPanel, Snapshot } from './snapshot-types.js';

export interface RangoPedido { from: string; to: string; preset: string }

/** Lo elegido en el desplegable: todas, o unos ids (que pueden llevar "sin_asignar"). */
export type SeleccionPersonas = 'todas' | string[];

export const SIN_ASIGNAR = 'sin_asignar';

/** La seleccion como texto estable ('todas' o los ids ordenados y separados por comas). */
export function claveSeleccion(sel: SeleccionPersonas): string {
  if (sel === 'todas') return 'todas';
  const ids = [...new Set(sel.map((s) => s.trim()).filter(Boolean))].sort();
  return ids.length === 0 || ids.includes('todas') ? 'todas' : ids.join(',');
}

/** La seleccion a partir de su texto. Lo que no se entiende es "Todas". */
export function seleccionDeClave(clave: string | null | undefined): SeleccionPersonas {
  if (!clave || clave === 'todas') return 'todas';
  const ids = clave.split(',').map((s) => s.trim()).filter(Boolean);
  return ids.length === 0 || ids.includes('todas') ? 'todas' : [...new Set(ids)].sort();
}

/**
 * Lo elegido, contra la lista de personas que se pueden elegir. Es la misma regla
 * que aplica el servidor: un id que no esta en la lista no cuenta ("Sin asignar"
 * siempre vale); si no queda ninguno, todas. Y si estan todas marcadas una a una,
 * es "Todas" (es la misma suma).
 */
export function normalizarSeleccion(sel: SeleccionPersonas, personas: readonly PersonaPanel[] | undefined): SeleccionPersonas {
  if (sel === 'todas' || !personas || personas.length === 0) return sel === 'todas' ? 'todas' : seleccionDeClave(claveSeleccion(sel));
  const lista = new Set(personas.map((p) => p.id));
  const validas = [...new Set(sel)].filter((id) => lista.has(id) || id === SIN_ASIGNAR).sort();
  if (validas.length === 0) return 'todas';
  if (validas.length === lista.size && validas.every((id) => lista.has(id))) return 'todas';
  return validas;
}

/** Lo que se pide al servidor para el snapshot. */
export function paramsDelSnapshot(sel: SeleccionPersonas, range: RangoPedido): URLSearchParams {
  const q = new URLSearchParams({ preset: range.preset, personas: claveSeleccion(sel) });
  q.set('from', range.from);
  q.set('to', range.to);
  if (range.preset === 'custom') { q.set('desde', range.from); q.set('hasta', range.to); }
  // Solo para un servidor de antes: sin `personas`, "team" es todas.
  q.set('scope', 'team');
  return q;
}

/** Lo que se pide para una lista: el filtro que aplico el snapshot pintado. */
export function paramsDeLista(snapshot: Snapshot | null, sel: SeleccionPersonas, range: RangoPedido): URLSearchParams {
  const f = snapshot?.filtro;
  if (!f) {
    const q = new URLSearchParams({ scope: 'team', personas: claveSeleccion(sel) });
    q.set('from', range.from);
    q.set('to', range.to);
    return q;
  }
  const q = new URLSearchParams({ preset: 'custom', desde: f.desde, hasta: f.hasta, from: f.desde, to: f.hasta, scope: 'team' });
  q.set('personas', f.personas === 'todas' ? 'todas' : f.personas.join(','));
  return q;
}

/**
 * El alcance de la forma vieja ("me" o "team") que equivale a lo elegido, para lo
 * que aun habla asi (la lista de ganadas de Zoho, los objetivos de un snapshot de
 * antes): solo uno mismo es "me"; lo demas, "team".
 */
export function alcanceCompat(sel: SeleccionPersonas, personas: readonly PersonaPanel[] | undefined): 'me' | 'team' {
  if (sel === 'todas' || sel.length !== 1) return 'team';
  return personas?.find((p) => p.id === sel[0])?.yo ? 'me' : 'team';
}

// ── Recordar lo elegido (por quien mira) ─────────────────────────────────────

/** La clave del navegador donde se guarda lo elegido por este usuario en este inquilino. */
export function claveGuardado(clientId: string, userId: string): string {
  return `fluxo.panel.personas:${clientId}:${userId}`;
}

function almacen(): Storage | null {
  try {
    const w = (globalThis as { window?: { localStorage?: Storage } }).window;
    return w?.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Lo guardado, o "todas" si no hay nada (o no se puede leer). */
export function leerSeleccionGuardada(clave: string): string {
  try {
    const v = almacen()?.getItem(clave);
    return v ? claveSeleccion(seleccionDeClave(v)) : 'todas';
  } catch {
    return 'todas';
  }
}

/** Guarda lo elegido. Si no se puede, no pasa nada: la proxima vez se vera "Todas". */
export function guardarSeleccion(clave: string, sel: SeleccionPersonas): void {
  try {
    const a = almacen();
    if (!a) return;
    const texto = claveSeleccion(sel);
    if (texto === 'todas') a.removeItem(clave); else a.setItem(clave, texto);
  } catch {
    // Sin almacenamiento (ventana privada, iframe bloqueado): se queda en memoria.
  }
}
